/**
 * PREGNANCY CALENDAR — SINGLE SOURCE OF TRUTH
 *
 * This file defines the complete 280-day / 40-week / 9-month pregnancy mapping.
 * Import utilities from here in the CMS and mirror this mapping in Flutter.
 * Do NOT hard-code month/week/day boundaries anywhere else.
 *
 * Mapping: Standard OB/GYN model
 *   Month 1 : Weeks  1–4  | Days   1–28
 *   Month 2 : Weeks  5–8  | Days  29–56
 *   Month 3 : Weeks  9–13 | Days  57–91
 *   Month 4 : Weeks 14–17 | Days  92–119
 *   Month 5 : Weeks 18–22 | Days 120–154
 *   Month 6 : Weeks 23–27 | Days 155–189
 *   Month 7 : Weeks 28–31 | Days 190–217
 *   Month 8 : Weeks 32–35 | Days 218–245
 *   Month 9 : Weeks 36–40 | Days 246–280
 */

// ─── Constants ────────────────────────────────────────────────────────────────

export const TOTAL_DAYS    = 280;
export const TOTAL_WEEKS   = 40;
export const TOTAL_MONTHS  = 9;
export const DAYS_PER_WEEK = 7;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface WeekInfo {
  weekNumber:  number;
  monthNumber: number;
  dayStart:    number;
  dayEnd:      number;
}

export interface MonthInfo {
  monthNumber: number;
  weekStart:   number;
  weekEnd:     number;
  dayStart:    number;
  dayEnd:      number;
  weeks:       WeekInfo[];
}

export interface DayInfo {
  dayNumber:   number;
  weekNumber:  number;
  monthNumber: number;
}

// ─── Raw month-to-week mapping ────────────────────────────────────────────────

/** Maps each month number to its [weekStart, weekEnd] (inclusive). */
const MONTH_WEEK_RANGES: Record<number, [number, number]> = {
  1: [1,  4],
  2: [5,  8],
  3: [9,  13],
  4: [14, 17],
  5: [18, 22],
  6: [23, 27],
  7: [28, 31],
  8: [32, 35],
  9: [36, 40],
};

// ─── Derived data (computed once at module load) ───────────────────────────────

/** All 40 weeks, indexed by weekNumber (1-based). */
const WEEKS: WeekInfo[] = Array.from({ length: TOTAL_WEEKS }, (_, i) => {
  const weekNumber = i + 1;
  const dayStart   = (weekNumber - 1) * DAYS_PER_WEEK + 1;
  const dayEnd     = weekNumber * DAYS_PER_WEEK;

  // Determine month for this week
  let monthNumber = 1;
  for (const [month, [wStart, wEnd]] of Object.entries(MONTH_WEEK_RANGES)) {
    if (weekNumber >= wStart && weekNumber <= wEnd) {
      monthNumber = Number(month);
      break;
    }
  }

  return { weekNumber, monthNumber, dayStart, dayEnd };
});

/** All 9 months, indexed by monthNumber (1-based). */
const MONTHS: MonthInfo[] = Array.from({ length: TOTAL_MONTHS }, (_, i) => {
  const monthNumber            = i + 1;
  const [weekStart, weekEnd]   = MONTH_WEEK_RANGES[monthNumber];
  const monthWeeks             = WEEKS.filter(w => w.monthNumber === monthNumber);
  const dayStart               = monthWeeks[0].dayStart;
  const dayEnd                 = monthWeeks[monthWeeks.length - 1].dayEnd;

  return { monthNumber, weekStart, weekEnd, dayStart, dayEnd, weeks: monthWeeks };
});

/** Day → { weekNumber, monthNumber } lookup table (1-indexed). */
const DAY_INFO_MAP: DayInfo[] = Array.from({ length: TOTAL_DAYS }, (_, i) => {
  const dayNumber  = i + 1;
  const weekNumber = Math.ceil(dayNumber / DAYS_PER_WEEK);
  const week       = WEEKS[weekNumber - 1];
  return { dayNumber, weekNumber, monthNumber: week.monthNumber };
});

// ─── Public lookup functions ───────────────────────────────────────────────────

/**
 * Get full information for a given day number (1–280).
 * Returns null for out-of-range values.
 */
export function getDayInfo(dayNumber: number): DayInfo | null {
  if (dayNumber < 1 || dayNumber > TOTAL_DAYS) return null;
  return DAY_INFO_MAP[dayNumber - 1];
}

/**
 * Get full information for a given week number (1–40).
 * Returns null for out-of-range values.
 */
export function getWeekInfo(weekNumber: number): WeekInfo | null {
  if (weekNumber < 1 || weekNumber > TOTAL_WEEKS) return null;
  return WEEKS[weekNumber - 1];
}

/**
 * Get full information for a given month number (1–9).
 * Returns null for out-of-range values.
 */
export function getMonthInfo(monthNumber: number): MonthInfo | null {
  if (monthNumber < 1 || monthNumber > TOTAL_MONTHS) return null;
  return MONTHS[monthNumber - 1];
}

/**
 * Returns the complete ordered list of all 9 months.
 */
export function getAllMonths(): MonthInfo[] {
  return [...MONTHS];
}

/**
 * Returns the complete ordered list of all 40 weeks.
 */
export function getAllWeeks(): WeekInfo[] {
  return [...WEEKS];
}

/**
 * Returns all day numbers (as DayInfo) that belong to a given month.
 */
export function getDaysInMonth(monthNumber: number): DayInfo[] {
  const month = getMonthInfo(monthNumber);
  if (!month) return [];
  return DAY_INFO_MAP.filter(d => d.monthNumber === monthNumber);
}

/**
 * Returns all day numbers (as DayInfo) that belong to a given week.
 */
export function getDaysInWeek(weekNumber: number): DayInfo[] {
  const week = getWeekInfo(weekNumber);
  if (!week) return [];
  return DAY_INFO_MAP.filter(d => d.weekNumber === weekNumber);
}

/**
 * Returns the day number immediately before the given day, or null if day 1.
 */
export function getPreviousDay(dayNumber: number): number | null {
  if (dayNumber <= 1) return null;
  return dayNumber - 1;
}

/**
 * Returns the day number immediately after the given day, or null if day 280.
 */
export function getNextDay(dayNumber: number): number | null {
  if (dayNumber >= TOTAL_DAYS) return null;
  return dayNumber + 1;
}

/**
 * Validates that a day number is within the valid range.
 */
export function isValidDayNumber(dayNumber: number): boolean {
  return Number.isInteger(dayNumber) && dayNumber >= 1 && dayNumber <= TOTAL_DAYS;
}

/**
 * Formats a day number as the 3-digit zero-padded Firestore document ID.
 * e.g. 1 → "001", 28 → "028", 280 → "280"
 */
export function dayNumberToDocId(dayNumber: number): string {
  return String(dayNumber).padStart(3, '0');
}

/**
 * Parses a Firestore document ID back to a day number.
 * e.g. "001" → 1, "280" → 280
 */
export function docIdToDayNumber(docId: string): number {
  return parseInt(docId, 10);
}

/**
 * Returns a human-readable label for a day.
 * e.g. getDayLabel(256) → "Day 256 of 280"
 */
export function getDayLabel(dayNumber: number): string {
  return `Day ${dayNumber} of ${TOTAL_DAYS}`;
}

/**
 * Returns a human-readable label for a week.
 * e.g. getWeekLabel(37) → "Week 37"
 */
export function getWeekLabel(weekNumber: number): string {
  return `Week ${weekNumber}`;
}

/**
 * Returns a human-readable label for a month.
 * e.g. getMonthLabel(9) → "Month 9"
 */
export function getMonthLabel(monthNumber: number): string {
  return `Month ${monthNumber}`;
}

/**
 * Returns the breadcrumb context string for a given day.
 * e.g. getDayContext("Mothers", 256) →
 *      "Mothers | Day 256 of 280 | Month 9 | Week 37"
 */
export function getDayContext(audienceLabel: string, dayNumber: number): string {
  const info = getDayInfo(dayNumber);
  if (!info) return '';
  return `${audienceLabel} | Day ${dayNumber} of ${TOTAL_DAYS} | Month ${info.monthNumber} | Week ${info.weekNumber}`;
}

/**
 * Returns week day range string.
 * e.g. getWeekDayRange(37) → "Days 253–259"
 */
export function getWeekDayRange(weekNumber: number): string {
  const week = getWeekInfo(weekNumber);
  if (!week) return '';
  return `Days ${week.dayStart}–${week.dayEnd}`;
}

/**
 * Returns month day range string.
 * e.g. getMonthDayRange(9) → "Days 246–280"
 */
export function getMonthDayRange(monthNumber: number): string {
  const month = getMonthInfo(monthNumber);
  if (!month) return '';
  return `Days ${month.dayStart}–${month.dayEnd}`;
}

/**
 * Returns the Firestore-ready calendar config object for writing to appConfig/pregnancyCalendar.
 * Called once during admin setup to seed the config for the Flutter app.
 */
export function getFirestoreCalendarConfig() {
  return {
    version:     '1.0.0',
    totalDays:   TOTAL_DAYS,
    totalWeeks:  TOTAL_WEEKS,
    totalMonths: TOTAL_MONTHS,
    months: MONTHS.map(m => ({
      monthNumber: m.monthNumber,
      weekStart:   m.weekStart,
      weekEnd:     m.weekEnd,
      dayStart:    m.dayStart,
      dayEnd:      m.dayEnd,
    })),
    weeks: WEEKS.map(w => ({
      weekNumber:  w.weekNumber,
      monthNumber: w.monthNumber,
      dayStart:    w.dayStart,
      dayEnd:      w.dayEnd,
    })),
  };
}
